// Build the 8-part CourtScribe transcript document model (Judicial Service of Ghana
// template) from the AI court summary. Shared by the on-screen SummarySection and the
// PDF export so both render exactly the same structure:
//   Part 1 Case Details · Part 2 Participants · Part 3 Transcript Generation Details
//   Part 4 Summary · Part 5 Transcript · Part 6 Review and Corrections Log
//   Part 7 Verification and Certification · Part 8 Distribution and Handling
import { formatSummary, isCourtSummaryDocument, formatDuration } from './transcriptionUtils';

const SECTION_TITLES = [
  'CASE / MATTER IDENTIFICATION',
  'PARTICIPANTS',
  'PURPOSE',
  'SUMMARY',
  'KEY POINTS',
  'DECISIONS AND ORDERS',
  'ACTION ITEMS',
  'NEXT DATE',
  'OPEN QUESTIONS',
  'RECORDING QUALITY',
  'TRANSCRIPT QUALITY',
  'BRIEF VERSION',
];

const PARTICIPANT_GROUPS = [
  'PRESIDING OFFICER',
  'COUNSEL AND LEGAL REPRESENTATIVES',
  'PARTIES',
  'WITNESSES, DEPONENTS, AND OTHERS NAMED OR PRESENT',
];

const NOT_STATED = 'Not stated';

const stripMarks = (line) => line.replace(/^[#*_\s]+|[*_:\s]+$/g, '').trim();

const plainText = (value) =>
  String(value ?? '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/^[-*•]\s+/, '')
    .trim();

/** Split a CourtScribe summary document into its heading sections. */
export const parseCourtSummarySections = (text) => {
  const sections = {};
  let current = null;
  String(text ?? '')
    .split('\n')
    .forEach((raw) => {
      const line = raw.trim();
      if (!line || /^[-_=*]{3,}$/.test(line)) return;
      const key = stripMarks(line).toUpperCase();
      if (SECTION_TITLES.includes(key)) {
        current = key;
        sections[current] = sections[current] || [];
        return;
      }
      const inline = line.match(/^[#*_\s]*([A-Z /]+?)[*_\s]*:\s*(.+)$/);
      if (inline && SECTION_TITLES.includes(inline[1].trim())) {
        current = inline[1].trim();
        sections[current] = sections[current] || [];
        sections[current].push(inline[2].trim());
        return;
      }
      if (!current) return;
      sections[current].push(line);
    });
  return sections;
};

const sectionItems = (lines) =>
  (lines || []).map(plainText).filter(Boolean);

const sectionParagraph = (lines) =>
  sectionItems(lines).join(' ').trim();

// "Suit No.: CT/2271/2026" -> { label, value }
const splitLabelValue = (line) => {
  const text = plainText(line);
  const match = text.match(/^([^:]{2,60}):\s*(.+)$/);
  if (!match) return { label: '', value: text };
  return { label: match[1].trim(), value: match[2].trim() };
};

const IDENTIFICATION_KEY_MAP = [
  [['suit', 'case no', 'case number', 'docket'], 'suit_number'],
  [['title', 'cause', 'matter'], 'case_title'],
  [['court', 'division'], 'court'],
  [['judge', 'presiding', 'bench'], 'judge'],
  [['registrar'], 'registrar'],
  [['hearing date', 'date'], 'hearing_date'],
  [['time', 'start', 'sitting'], 'hearing_time'],
  [['stage', 'proceedings'], 'stage'],
  [['appearance', 'counsel', 'for the'], 'appearances'],
];

const identifyField = (label) => {
  const lower = label.toLowerCase();
  for (const [needles, field] of IDENTIFICATION_KEY_MAP) {
    if (needles.some((n) => lower.includes(n))) return field;
  }
  return null;
};

const parseIdentification = (lines) => {
  const fields = {};
  (lines || []).forEach((line) => {
    const { label, value } = splitLabelValue(line);
    const field = label ? identifyField(label) : null;
    if (field && value && !fields[field]) fields[field] = value;
    else if (value) (fields.extra = fields.extra || []).push(value);
  });
  return fields;
};

// Participant lines may be "Role: Name — capacity" or plain names from
// summary_structured.parties_present.
const parseParticipant = (line) => {
  const text = plainText(line);
  const roleMatch = text.match(/^([A-Z][A-Za-z /,&-]{2,40}?):\s*(.+)$/);
  if (roleMatch) {
    const rest = roleMatch[2];
    const dash = rest.split(/\s+[—–-]\s+/);
    return {
      role: roleMatch[1].trim(),
      name: dash[0].trim(),
      notes: (dash[1] || '').trim() || 'Stated in the summary',
    };
  }
  const paren = text.match(/^(.*?)\s*\(([^)]{3,})\)\s*$/);
  if (paren) {
    return {
      role: paren[2].trim(),
      name: paren[1].trim(),
      notes: 'Named in the hearing',
    };
  }
  return { role: NOT_STATED, name: text, notes: 'Named in the hearing' };
};

// Build indicative elapsed times (hh:mm:ss) spread across the recording, like the
// reference template does for a summary-derived transcript.
const indicativeTimes = (count, durationSeconds) => {
  const total = durationSeconds > 0 ? durationSeconds : count * 30;
  const step = Math.max(10, Math.floor((total * 0.8) / Math.max(count, 1)));
  const pad = (n) => String(n).padStart(2, '0');
  return Array.from({ length: count }, (_, i) => {
    const secs = Math.min(5 + i * step, total);
    return `${pad(Math.floor(secs / 3600))}:${pad(Math.floor((secs % 3600) / 60))}:${pad(secs % 60)}`;
  });
};

const UNCERTAINTY_NEEDLES = [
  'not stated', 'unconfirmed', 'unclear', 'unknown', 'inaudible', 'possibly',
  'could not', 'not provided', 'not captured', 'spelling', 'inconsistent', 'low-confidence',
];

const collectFlags = (sections) => {
  const flags = [];
  ['OPEN QUESTIONS', 'RECORDING QUALITY', 'TRANSCRIPT QUALITY'].forEach((title) => {
    sectionItems(sections[title]).forEach((item) => {
      const lower = item.toLowerCase();
      if (UNCERTAINTY_NEEDLES.some((n) => lower.includes(n))) flags.push(item);
    });
  });
  return flags;
};

/**
 * Build the full 8-part document model from a summary payload + transcription record.
 * Returns null when the summary has no usable content to template.
 */
export const buildCourtSummaryDocument = (
  summary,
  transcription,
  isEditingSummary,
  editedSummary,
) => {
  const narrative = isEditingSummary
    ? String(editedSummary ?? '')
    : formatSummary(summary) || (typeof summary === 'string' ? summary : '');
  const merged = narrative && typeof narrative === 'string' ? narrative : '';
  if (!merged.trim() || !isUsableSummaryText(summary, merged)) return null;

  const sections = isCourtSummaryDocument(merged)
    ? parseCourtSummarySections(merged)
    : legacySections(merged);

  const identification = parseIdentification(sections['CASE / MATTER IDENTIFICATION']);
  const structured = (summary && typeof summary === 'object') ? summary : {};
  const durationSeconds = Number(transcription?.duration_seconds) || 0;

  const caseTitle = identification.case_title
    || (transcription?.title ? plainText(transcription.title) : NOT_STATED);
  const suitNumber = identification.suit_number || transcription?.case_reference || 'Not applicable';
  const registrarOnDuty = identification.registrar
    || 'Court Registrar on duty (name endorsed at verification, Part 7)';

  const participantLines = [
    ...(sections['PARTICIPANTS'] || []).map(plainText).filter(Boolean),
    ...(Array.isArray(structured.parties_present) ? structured.parties_present : []),
  ];
  const seen = new Set();
  const participants = participantLines
    .map((line) => {
      const parsed = typeof line === 'string' ? parseParticipant(line) : null;
      return parsed || { role: NOT_STATED, name: String(line), notes: 'Named in the hearing' };
    })
    .filter((p) => {
      const key = p.name.toLowerCase();
      if (!p.name || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  if (participants.length === 0) {
    participants.push({ role: 'Participants', name: NOT_STATED, notes: 'No participants were named in the summary' });
  }

  const rulings = sectionItems(sections['DECISIONS AND ORDERS']);
  const keyPoints = sectionItems(sections['KEY POINTS']);
  const actionItems = sectionItems(sections['ACTION ITEMS']);
  const openQuestions = sectionItems(sections['OPEN QUESTIONS']);
  const nextDate = sectionParagraph(sections['NEXT DATE'])
    || structured.next_hearing_date
    || NOT_STATED;

  // Part 5 rows derived from the summary events (summaries only, per product decision).
  const eventSources = rulings.length ? rulings : keyPoints;
  const proceedingsText = sectionParagraph(sections['SUMMARY']);
  const eventLines = eventSources.length ? eventSources : (proceedingsText ? [proceedingsText] : []);
  const times = indicativeTimes(Math.max(eventLines.length, 1), durationSeconds);
  const transcriptRows = eventLines.map((text, i) => ({
    time: times[i] || '00:00:00',
    speaker: speakerForEvent(text),
    proceedings: text,
  }));
  if (transcriptRows.length === 0) {
    transcriptRows.push({ time: '00:00:00', speaker: 'Court', proceedings: 'No proceedings recorded.' });
  }

  const flags = collectFlags(sections);

  return {
    meta: {
      courtName: identification.court || NOT_STATED,
      documentTitle: 'COURT SCRIBE — AI-GENERATED SUMMARY',
      bannerNotice:
        'For review by the Judge and Registrar only. Not an official record of the proceedings until verified and certified in Part 7.',
      transcriptId: transcription?.case_reference
        || (transcription?.title ? plainText(transcription.title) : 'Court hearing'),
      caseTitle,
      suitNumber,
      draftLabel: 'INTERNAL DRAFT — AI-GENERATED',
    },
    part1: {
      rows: [
        ['Suit / Case No.', suitNumber],
        ['Title of Case', caseTitle],
        ['Court / Division', identification.court || NOT_STATED],
        ['Presiding Judge', identification.judge || NOT_STATED],
        ['Court Registrar', registrarOnDuty],
        ['Hearing Date', identification.hearing_date || transcription?.date || NOT_STATED],
        ['Hearing Start / End Time', identification.hearing_time
          || `Start 00:00:00; end approx. ${formatDuration(durationSeconds) || NOT_STATED} (indicative elapsed times)`],
        ['Stage of Proceedings', identification.stage || sectionParagraph(sections['PURPOSE']) || NOT_STATED],
        ['Appearances', identification.appearances
          || participants.map((p) => `${p.role}: ${p.name}`).filter((v, i, a) => a.indexOf(v) === i).join('; ')
          || NOT_STATED],
        ...(identification.extra || []).map((value, i) => [`Note ${i + 1}`, value]),
      ],
    },
    part2: {
      head: ['Role', 'Name', 'Capacity / Notes'],
      rows: participants,
    },
    part3: {
      rows: [
        ['Transcript ID', `${(transcription?.case_reference || '').trim() || plainText(transcription?.title || 'Court hearing')}`],
        ['Version', 'AI-generated summary (source status: complete)'],
        ['Source Recording', `${transcription?.title || 'Court hearing recording'} of ${identification.hearing_date || transcription?.date || 'date not stated'}`],
        ['Recording Duration', durationSeconds ? `Approximately ${formatDuration(durationSeconds)}` : NOT_STATED],
        ['AI System Used', 'Snwolley CourtScribe'],
        ['Date and Time Generated', new Date().toLocaleString()],
        ['Generated / Submitted By', 'Snwolley CourtScribe (automated system), submitted to the Court Registry for review'],
        ['Language(s) of Proceedings', transcription?.language || 'English'],
        ['Overall Review Status', 'Awaiting review'],
      ],
      disclaimer:
        'This transcript was produced by an automated speech-to-text system from the source recording. It may contain errors in wording, speaker attribution, names, numbers, dates and legal terms, and must be checked against the recording before any reliance is placed on it.',
    },
    part4: {
      purpose: sectionParagraph(sections['PURPOSE']) || 'Not stated.',
      narrative: sectionParagraph(sections['SUMMARY']) || sectionParagraph(sections['BRIEF VERSION']) || 'No narrative summary was produced.',
      keyPoints,
      decisions: rulings.length ? rulings : ['No decisions or orders were recorded in the summary.'],
      actionItems,
      nextDate,
      openQuestions,
    },
    part5: {
      note: 'Note: this transcript was prepared from the AI-generated summary of the hearing, not from a verbatim record. The timestamps shown (hh:mm:ss) are indicative elapsed times assigned in the order of events and must be corrected against the recording.',
      head: ['Time', 'Speaker', 'Proceedings'],
      rows: transcriptRows,
      conventions:
        '[inaudible] — speech could not be made out.  [unclear: word?] — best guess, to be checked.  [overlapping speech] — more than one speaker at once.  [flag] — passage the AI system marked as low-confidence.',
    },
    part6: {
      intro: 'Record every correction made to the AI draft after checking against the recording.',
      head: ['Item', 'Flagged point', 'Reason / Source', 'Status'],
      rows: flags.map((text, i) => ({
        item: i + 1,
        correction: text,
        reason: 'AI flag: uncertain or unstated in the summary; confirm against recording',
        initials: 'Pending',
      })),
      reviewerNotes: [
        'This draft was generated from an AI summary, not a verbatim recording; wording must be verified against the recording.',
        'Timestamps are indicative elapsed times and must be replaced with the actual times from the recording.',
        'The corrections in Part 6 are proposals only and take effect when verified and initialled by the Court Registrar.',
      ],
    },
    part7: {
      intro: 'The statements below are made only by the officer named in each block, after comparison of this transcript with the source recording.',
      registrarStatement:
        'I have compared this transcript with the source recording and have made the corrections recorded in Part 6. In my view it is a true and accurate record of the proceedings, save for the passages noted.',
      judgeStatement:
        'I have reviewed this transcript and approved it for the purpose stated in Part 8 (Distribution and Handling).',
    },
    part8: {
      rows: [
        ['Classification', 'Restricted — Internal Judicial Use'],
        ['Permitted Purpose', 'Judicial review and registry reference only. Not for release to parties unless certified.'],
        ['Circulation', 'Presiding Judge; Court Registrar; others only by authority of the Judge'],
        ['Retention', 'Per Judicial Service records policy; hold as part of the case file until certified or superseded'],
      ],
    },
  };
};

const isUsableSummaryText = (summary, text) => {
  if (text && text.trim()) return true;
  return Boolean(summary && typeof summary === 'object'
    && (summary.parties_present?.length || summary.key_rulings?.length || summary.next_hearing_date));
};

// Simple speaker guess for the Part 5 table, from the event wording.
const speakerForEvent = (text) => {
  const lower = text.toLowerCase();
  if (/prosecution|plaintiff|republic|asp|police/.test(lower)) return 'Prosecution';
  if (/defence|defense|accused|counsel for the accused/.test(lower)) return 'Defence Counsel';
  if (/court|judge|registrar|adjourn|ordered|granted/.test(lower)) return 'Court';
  return 'Court';
};

// Older narrative-only summaries (Hearing summary / Parties present / Key rulings /
// Next hearing from formatSummary) mapped onto the same section shape.
const legacySections = (text) => {
  const HEAD_MAP = {
    'hearing summary': 'SUMMARY',
    'parties present': 'PARTICIPANTS',
    'key rulings': 'DECISIONS AND ORDERS',
    'next hearing': 'NEXT DATE',
  };
  const sections = {};
  let current = 'SUMMARY';
  String(text).split('\n').forEach((raw) => {
    const line = raw.trim();
    if (!line) return;
    const head = line.match(/^\*\*(.+?)\*\*/);
    if (head && HEAD_MAP[head[1].toLowerCase()]) {
      current = HEAD_MAP[head[1].toLowerCase()];
      const rest = line.slice(head[0].length).replace(/^:\s*/, '').trim();
      if (rest) {
        sections[current] = sections[current] || [];
        sections[current].push(rest);
      }
      return;
    }
    sections[current] = sections[current] || [];
    sections[current].push(line);
  });
  return sections;
};
